import React, {useEffect, useId, useRef, useState} from 'react';
import {connect} from 'react-redux';
import {
    Avatar, Badge, Box, Button, Checkbox, CircularProgress, IconButton, InputAdornment,
    LinearProgress, List, ListItem, Popover, TextField, Tooltip, Typography, useMediaQuery, useTheme
} from '@mui/material';
import {
    ArrowDropDownRounded,
    ClearRounded,
    CloseRounded,
    SearchRounded,
    SearchOffRounded,
    WorkspacesRounded
} from '@mui/icons-material';
import {alpha} from '@mui/material/styles';
import {workspaceActions} from '../../store/actions/workspaceAction';
import {getSettingsColors, settingsInputStyles} from '../settings/SettingsDesign';

const selectedIds = workspaces => workspaces.filter(item => item.selected).map(item => item.id);
const initials = name => name.trim().split(/\s+/).slice(0, 2).map(word => word[0]).join('').toUpperCase();

export function WorkspaceDropdown({workspace, getUserWorkspaces, updateSelectedUserWorkspaces, compact: compactProp}) {
    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down(625));
    const compact = compactProp ?? smallScreen;
    const workspaces = workspace?.userWorkspaces || [];
    const applied = workspaces.filter(item => item.selected);
    const [anchorEl, setAnchorEl] = useState(null);
    const [checked, setChecked] = useState(() => selectedIds(workspaces));
    const [search, setSearch] = useState('');
    const [applying, setApplying] = useState(false);
    const pendingApply = useRef(false);
    const previousUpdate = useRef(null);
    const id = useId();
    const loading = !!workspace?.loading?.getUserWorkspaces;
    const busy = loading || applying;
    const allChecked = workspaces.length > 0 && checked.length === workspaces.length;
    const dirty = checked.length !== applied.length || applied.some(item => !checked.includes(item.id));
    const filtered = workspaces.filter(item => (item.name || '').toLowerCase().includes(search.trim().toLowerCase()));
    const summary = applied.length === 1 ? applied[0].name || 'Workspace' : applied.length === workspaces.length && workspaces.length > 0
        ? 'All workspaces' : applied.length > 1 ? `${applied.length} workspaces` : 'Workspaces';

    useEffect(() => {
        setChecked(selectedIds(workspace?.userWorkspaces || []));
    }, [workspace?.userWorkspaces]);

    useEffect(() => {
        // Failed requests retain the draft; only this menu's save can close it.
        if (pendingApply.current && !loading) {
            pendingApply.current = false;
            setApplying(false);
            if (workspace?.updated !== previousUpdate.current) setAnchorEl(null);
        }
    }, [loading, workspace?.updated]);

    const openMenu = event => {
        setChecked(selectedIds(workspaces));
        setSearch('');
        setAnchorEl(event.currentTarget);
        getUserWorkspaces();
    };
    const closeMenu = () => {
        if (!applying) setAnchorEl(null);
    };
    const toggle = value => setChecked(previous => previous.includes(value) ? previous.filter(item => item !== value) : [...previous, value]);
    const applyChanges = () => {
        if (busy || !dirty || checked.length === 0) return;
        previousUpdate.current = workspace?.updated;
        pendingApply.current = true;
        setApplying(true);
        updateSelectedUserWorkspaces(checked, workspaces.map(item => ({id: item.id, selected: checked.includes(item.id)})));
    };

    return <Box sx={{minWidth: 0, color: 'text.primary'}}>
        <Tooltip title={`Workspaces · ${summary}`}>
            {compact ? <IconButton id="workspace-multi-select-1" onClick={openMenu} aria-label="Choose workspaces"
                                   aria-haspopup="dialog" aria-expanded={!!anchorEl}
                                   aria-controls={anchorEl ? `${id}-popover` : undefined}
                                   sx={{width: 38, height: 38, color: 'text.primary', borderRadius: '10px'}}>
                <Badge badgeContent={applied.length} color="primary" sx={{
                    '& .MuiBadge-badge': {
                        fontSize: 9, fontWeight: 600, minWidth: 15, height: 15, px: 0.5,
                        color: theme => theme.palette.mode === 'dark' ? theme.palette.background.darkBlue : theme.palette.primary.contrastText
                    }
                }}><WorkspacesRounded sx={{fontSize: 21}}/></Badge>
            </IconButton> : <Button id="workspace-multi-select-2" onClick={openMenu} color="inherit"
                                    aria-label="Choose workspaces" aria-haspopup="dialog" aria-expanded={!!anchorEl}
                                    aria-controls={anchorEl ? `${id}-popover` : undefined}
                                    startIcon={<WorkspacesRounded sx={{fontSize: 19}}/>}
                                    endIcon={<ArrowDropDownRounded/>}
                                    sx={{
                                        height: 36, maxWidth: '100%', px: 1.25, borderRadius: '10px', textTransform: 'none',
                                        bgcolor: theme => getSettingsColors(theme).inset, color: 'text.primary',
                                        '&:hover': {bgcolor: theme => alpha(theme.palette.primary.main, 0.08)}, '& .MuiButton-startIcon': {color: 'primary.main'}
                                    }}>
                <Box component="span"
                     sx={{maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, fontWeight: 500}}>{summary}</Box>
            </Button>}
        </Tooltip>
        <Popover id={`${id}-popover`} open={!!anchorEl} anchorEl={anchorEl} onClose={closeMenu} marginThreshold={8}
                 anchorOrigin={{vertical: 'bottom', horizontal: compact ? 'right' : 'left'}}
                 transformOrigin={{vertical: 'top', horizontal: compact ? 'right' : 'left'}}
                 slotProps={{
                     paper: {
                         role: 'dialog', 'aria-labelledby': `${id}-title`, 'aria-describedby': `${id}-description`, sx: {
                             ...settingsInputStyles, width: 360, maxWidth: 'calc(100vw - 16px)', maxHeight: 'calc(100% - 16px)',
                             display: 'flex', flexDirection: 'column', borderRadius: '14px', border: '1px solid', borderColor: 'divider',
                             boxShadow: 'none', bgcolor: theme => getSettingsColors(theme).card, backgroundImage: 'none', color: 'text.primary'
                         }
                     }
                 }}>
            <Box sx={{display: 'flex', gap: 1.5, px: 2, pt: 2, pb: 1.5, flexShrink: 0}}>
                <Box sx={{flex: 1, minWidth: 0}}>
                    <Typography id={`${id}-title`} component="h2"
                                sx={{fontSize: 16, fontWeight: 600}}>Workspaces</Typography>
                    <Typography id={`${id}-description`} sx={{fontSize: 12, mt: 0.5, color: 'text.secondary'}}>Choose
                        which workspaces to show.</Typography>
                </Box>
                <IconButton aria-label="Close workspaces" size="small" onClick={closeMenu} disabled={applying}
                            sx={{color: 'text.secondary', alignSelf: 'flex-start'}}>
                    <CloseRounded fontSize="small"/>
                </IconButton>
            </Box>
            {workspaces.length > 5 && <Box sx={{px: 2, pb: 1.5, flexShrink: 0}}>
                <TextField size="small" fullWidth value={search} onChange={event => setSearch(event.target.value)}
                           placeholder="Search workspaces"
                           disabled={busy}
                           sx={{m: 0, '& .MuiOutlinedInput-root': {height: 38}, '& .MuiOutlinedInput-input': {fontSize: {xs: 16, sm: 13}}}}
                           slotProps={{
                               htmlInput: {'aria-label': 'Search workspaces'}, input: {
                                   startAdornment: <InputAdornment position="start"><SearchRounded
                                       sx={{fontSize: 18, color: 'text.secondary'}}/></InputAdornment>,
                                   endAdornment: search ?
                                       <InputAdornment position="end"><IconButton aria-label="Clear workspace search"
                                                                                  size="small"
                                                                                  onClick={() => setSearch('')}
                                                                                  disabled={busy}>
                                           <ClearRounded sx={{fontSize: 16}}/>
                                       </IconButton></InputAdornment> : undefined
                               }
                           }}/>
            </Box>}
            {workspaces.length > 0 && <Box component="label" sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', mx: 2, px: 1.25,
                py: 0.5, borderRadius: '10px', bgcolor: theme => getSettingsColors(theme).inset, cursor: busy ? 'default' : 'pointer', flexShrink: 0
            }}>
                <Typography component="span" sx={{fontSize: 12, fontWeight: 500}}>All workspaces</Typography>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                    <Typography component="span"
                                sx={{fontSize: 11, color: 'text.secondary'}}>{checked.length}/{workspaces.length}</Typography>
                    <Checkbox size="small" checked={allChecked} indeterminate={checked.length > 0 && !allChecked}
                              disabled={busy}
                              onChange={() => setChecked(allChecked ? [] : workspaces.map(item => item.id))}
                              slotProps={{input: {'aria-label': 'Select all workspaces'}}} sx={{p: 0.5}}/>
                </Box>
            </Box>}
            {busy && <LinearProgress aria-label={applying ? 'Applying workspaces' : 'Loading workspaces'}
                                     sx={{mt: 1, flexShrink: 0, height: 2}}/>}
            <Box aria-busy={busy} sx={{minHeight: 0, minWidth: 0, overflowY: 'auto', overflowX: 'hidden', maxHeight: 300, px: 2, py: 1.25}}>
                {filtered.length > 0 ? <List disablePadding sx={{display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 0.5, minWidth: 0}}>
                    {filtered.map(item => {
                        const selected = checked.includes(item.id);
                        return <ListItem key={item.id} disablePadding sx={{minWidth: 0}}>
                            <Box component="label" sx={{
                                display: 'flex', alignItems: 'center', width: '100%', minWidth: 0, gap: 1.25, px: 1.25, py: 1, borderRadius: '10px',
                                cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
                                bgcolor: theme => selected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                                '&:hover': {bgcolor: theme => alpha(theme.palette.primary.main, 0.06)},
                                '&:focus-within': {outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2}
                            }}>
                                <Avatar variant="rounded" sx={{
                                    width: 30, height: 30, borderRadius: '8px', fontSize: 11, fontWeight: 600, flexShrink: 0,
                                    bgcolor: theme => selected ? alpha(theme.palette.primary.main, 0.12) : getSettingsColors(theme).inset,
                                    color: selected ? 'primary.main' : 'text.secondary'
                                }}>{initials(item.name || 'Workspace')}</Avatar>
                                <Typography component="span"
                                            sx={{flex: 1, minWidth: 0, fontSize: 13, fontWeight: selected ? 500 : 400, whiteSpace: 'normal', overflowWrap: 'anywhere'}}>{item.name || 'Workspace'}</Typography>
                                <Checkbox size="small" checked={selected} disabled={busy}
                                          onChange={() => toggle(item.id)}
                                          slotProps={{input: {'aria-label': `Select ${item.name || 'workspace'}`}}}
                                          sx={{p: 0.5, flexShrink: 0}}/>
                            </Box>
                        </ListItem>;
                    })}
                </List> : <Box sx={{textAlign: 'center', py: 3, px: 1}}>
                    {loading ?
                        <Typography sx={{fontSize: 13, color: 'text.secondary'}}>Loading workspaces…</Typography> : <>
                            {search ? <SearchOffRounded sx={{fontSize: 30, color: 'text.secondary', mb: 0.75}}/> :
                                <WorkspacesRounded sx={{fontSize: 30, color: 'text.secondary', mb: 0.75}}/>}
                            <Typography
                                sx={{fontSize: 13, fontWeight: 500}}>{search ? 'No workspaces found' : 'No workspaces available'}</Typography>
                            <Typography
                                sx={{fontSize: 12, color: 'text.secondary', mt: 0.5}}>{search ? 'Try a different workspace name.' : 'Your available workspaces will appear here.'}</Typography>
                        </>}
                </Box>}
            </Box>
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 2, py: 1.25,
                bgcolor: theme => getSettingsColors(theme).inset, flexShrink: 0
            }}>
                <Typography
                    sx={{flex: 1, minWidth: 0, whiteSpace: 'normal', fontSize: 11, color: checked.length === 0 && workspaces.length > 0 ? 'error.main' : 'text.secondary'}}>
                    {checked.length === 0 && workspaces.length > 0 ? 'Select at least one workspace' : `${checked.length} selected`}
                </Typography>
                <Box sx={{display: 'flex', gap: 0.5, flexShrink: 0}}>
                    <Button color="inherit" size="small" onClick={closeMenu} disabled={applying}
                            sx={{minWidth: 0, px: 1, textTransform: 'none'}}>Cancel</Button>
                    <Button variant="contained" size="small" onClick={applyChanges}
                            disabled={busy || !dirty || checked.length === 0}
                            startIcon={applying ? <CircularProgress size={12} color="inherit"/> : undefined}
                            sx={{minHeight: 34, px: 1.5, borderRadius: '8px', textTransform: 'none'}}>{applying ? 'Applying…' : 'Apply'}</Button>
                </Box>
            </Box>
        </Popover>
    </Box>;
}

export default connect(
    state => ({
        workspace: state.workspace
    }),
    {
        getUserWorkspaces: workspaceActions.getUserWorkspaces,
        updateSelectedUserWorkspaces: workspaceActions.updateSelectedUserWorkspaces
    }
)(WorkspaceDropdown);
